import React, { useEffect, useCallback, useRef, useState } from 'react';
import {
   List,
   ListItem,
   ListItemAvatar,
   ListItemText,
   Avatar,
   Typography,
   Box,
   Button,
   Divider,
   CircularProgress,
   Paper,
   ButtonGroup,
   Snackbar,
   Alert,
} from '@mui/material';
import {
   Email as EmailIcon,
   Comment as CommentIcon,
   Group as GroupIcon,
   Photo as PhotoIcon,
   Edit as EditIcon,
   NotificationsActive as NotificationIcon,
   LocationOn as LocationOnIcon,
   Chat as ChatIcon,
   PhotoLibrary as AlbumIcon,
   PersonAdd as NetworkIcon,
   Flight as TripIcon,
   Event as EventIcon,
   Check as CheckIcon,
   Visibility as ViewIcon,
   ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../contexts/NotificationContext';
import { useWebSocketChat } from '../contexts/WebSocketChatContext';
import { Notification, NotificationType } from '../types/Notification';
import { formatDistanceToNow } from 'date-fns';
import { PendingInvitation } from '../services/pendingInvitationsService';
import { getAuthHeaders } from '../utils/auth';
import { formatLocationDisplay } from '../utils/formatting';
import { useUser } from '../contexts/UserContext';

interface NotificationListProps {
   onNotificationClick?: () => void;
   maxHeight?: number | string;
}

const NotificationList: React.FC<NotificationListProps> = ({
   onNotificationClick,
   maxHeight = 400,
}) => {
   const navigate = useNavigate();
   const { currentUser } = useUser();
   const {
      notifications,
      markAsRead,
      unreadCount,
      isLoading,
      error,
      hasMore,
      isLoadingMore,
      loadMoreNotifications,
   } = useNotifications();

   const {
      createOneOnOneChat,
      setActiveChannel,
      createTripGroupChat,
      createEventGroupChat,
      channels,
   } = useWebSocketChat();
   const scrollContainerRef = useRef<HTMLDivElement>(null);

   // State for user feedback
   const [toast, setToast] = useState<{
      open: boolean;
      message: string;
      severity: 'success' | 'error' | 'info';
   }>({
      open: false,
      message: '',
      severity: 'info',
   });

   const [loadingChatId, setLoadingChatId] = useState<string | null>(null);
   const [loadingInvitationView, setLoadingInvitationView] = useState<string | null>(null);

   // Toast helper function
   const showToast = (message: string, severity: 'success' | 'error' | 'info' = 'info') => {
      setToast({ open: true, message, severity });
   };

   const closeToast = () => {
      setToast({ open: false, message: '', severity: 'info' });
   };

   useEffect(() => {
      // Log error for debugging purposes
      if (error) {
         console.error('NotificationList error:', error);
      }
   }, [error]);

   // Scroll handler for infinite loading
   const handleScroll = useCallback(
      (event: React.UIEvent<HTMLDivElement>) => {
         const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
         const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100; // 100px threshold

         if (isNearBottom && hasMore && !isLoadingMore) {
            loadMoreNotifications();
         }
      },
      [hasMore, isLoadingMore, loadMoreNotifications]
   );

   // Get icon based on notification type
   const getNotificationIcon = (type: NotificationType) => {
      switch (type) {
         case 'MESSAGE':
            return <ChatIcon />;
         case 'COMMENT':
            return <CommentIcon />;
         case 'TRIP_INVITATION':
            return <TripIcon />;
         case 'EVENT_INVITATION':
            return <EventIcon />;
         case 'ALBUM_INVITATION':
            return <AlbumIcon />;
         case 'NETWORK_INVITATION':
            return <NetworkIcon />;
         case 'PHOTO_UPLOAD':
            return <PhotoIcon />;
         case 'TRIP_UPDATE':
            return <EditIcon />;
         case 'EVENT_UPDATE':
            return <EditIcon />;
         case 'TRIP_INVITATION_ACCEPTED':
            return <CheckIcon />;
         case 'EVENT_INVITATION_ACCEPTED':
            return <CheckIcon />;
         default:
            return <NotificationIcon />;
      }
   };

   // Get background color based on notification read status
   const getBackgroundColor = (isRead: boolean) => {
      return isRead ? 'transparent' : 'rgba(25, 118, 210, 0.08)';
   };

   // Handle notification click based on type
   const handleNotificationClick = async (notification: Notification) => {
      try {
         // Check if this is a pending invitation
         const isInvitation = ['TRIP_INVITATION', 'EVENT_INVITATION', 'ALBUM_INVITATION', 'NETWORK_INVITATION'].includes(notification.type);
         
         if (isInvitation && notification.invitationData) {
            // For invitations, navigate to the invitation page
            await handleViewInvitation(notification);
            return;
         }

         // For regular notifications, mark as read
         await markAsRead(notification.id);

         // Navigation disabled to prevent logout issues
         // Users can view notification details without being redirected
         console.log('Notification clicked:', notification.type, notification.sourceType);

         // Close notification panel if callback provided
         if (onNotificationClick) {
            onNotificationClick();
         }
      } catch (err) {
         console.error('Error handling notification click:', err);
      }
   };

   // Handle chat navigation for message notifications
   const handleChatNavigation = async (notification: Notification) => {
      try {
         // Debug: Log notification data to help troubleshoot group chats
         console.log('🔍 Handling chat navigation for notification:', {
            type: notification.type,
            sourceType: notification.sourceType,
            sourceId: notification.sourceId,
            sourceName: notification.sourceName,
            data: notification.data,
            senderInfo: notification.senderInfo,
         });

         // Set loading state
         setLoadingChatId(notification.sourceId || notification.data?.roomId || 'unknown');

         // Show loading toast
         showToast('Opening chat...', 'info');

         let chatRoom = null;
         let successMessage = '';

         // Check if this notification has a roomId (group chat)
         const roomId = notification.data?.roomId;

         if (roomId) {
            // This is a group chat - find the existing chat room by roomId
            console.log('🔍 Looking for existing group chat with roomId:', roomId);

            // Find the chat room in the existing channels
            chatRoom = channels.find((room) => room.id === roomId);

            if (chatRoom) {
               const entityName =
                  notification.data?.entityName ||
                  notification.sourceName ||
                  chatRoom.name ||
                  'Group Chat';
               successMessage = `${entityName} opened successfully`;
            } else {
               console.log('⚠️ Group chat room not found, trying to create based on entity type');

               // Try to create based on entity type
               const entityType = notification.data?.entityType;
               let entityName = notification.data?.entityName || notification.sourceName;

               // Additional fallback: try to extract entity name from room ID
               if (!entityName) {
                  if (roomId.includes('trip_')) {
                     entityName = 'Trip Chat';
                  } else if (roomId.includes('event_')) {
                     entityName = 'Event Chat';
                  } else {
                     entityName = 'Group Chat';
                  }
               }

               if (entityType === 'trip') {
                  // Extract trip ID from room ID or sourceId
                  const tripId =
                     notification.sourceId || roomId.replace('trip_', '').replace('_group', '');
                  const tripTitle = entityName || 'Trip Chat';

                  chatRoom = await createTripGroupChat(tripId, tripTitle);
                  successMessage = `${tripTitle} group chat opened successfully`;
               } else if (entityType === 'event') {
                  // Extract event ID from room ID or sourceId
                  const eventId =
                     notification.sourceId || roomId.replace('event_', '').replace('_group', '');

                  chatRoom = await createEventGroupChat(eventId);
                  successMessage = `${entityName || 'Event'} group chat opened successfully`;
               } else {
                  showToast(
                     `Unable to open group chat - unknown entity type: ${entityType}`,
                     'error'
                  );
                  return;
               }
            }
         } else {
            // This is a one-on-one chat
            const senderId = notification.senderInfo?.id || notification.data?.senderId;

            if (!senderId) {
               console.warn('No sender ID found in notification:', notification);
               showToast('Unable to open chat - sender information missing', 'error');
               return;
            }

            chatRoom = await createOneOnOneChat(senderId);
            const senderName = notification.senderInfo?.name || 'user';
            successMessage = `Chat with ${senderName} opened successfully`;
         }

         if (chatRoom) {
            // Set the active channel
            setActiveChannel(chatRoom);

            // Navigate to the communications page
            navigate('/communications');

            // Show success message
            showToast(successMessage, 'success');
         } else {
            console.error('Failed to create/find chat room for notification:', notification);
            showToast('Failed to open chat. Please try again.', 'error');
         }
      } catch (err) {
         console.error('Error navigating to chat:', err);
         showToast('Error opening chat. Please try again.', 'error');
      } finally {
         // Clear loading state
         setLoadingChatId(null);
      }
   };

   // Handle viewing invitation - generates token and navigates to invitation page
   const handleViewInvitation = async (notification: Notification) => {
      try {
         // Check if this is a pending invitation (has invitationData)
         if (!notification.invitationData) {
            showToast('This invitation is no longer available', 'error');
            return;
         }

         if (!currentUser?.email) {
            showToast('Unable to view invitation. Please log in again.', 'error');
            return;
         }

         setLoadingInvitationView(notification.id);
         showToast('Opening invitation...', 'info');

         const invitationType = notification.invitationData.invitationType;
         const invitationId = notification.invitationData.invitationId;

         // Generate token for the invitation
         const headers = await getAuthHeaders();
         let tokenEndpoint = '';

         switch (invitationType) {
            case 'album':
               tokenEndpoint = `${process.env.REACT_APP_API_URL}/api/albums/generate-token`;
               break;
            case 'trip':
            case 'event':
               tokenEndpoint = `${process.env.REACT_APP_API_URL}/api/invitation/generate-token`;
               break;
            case 'network':
               tokenEndpoint = `${process.env.REACT_APP_API_URL}/api/network/generate-token`;
               break;
            default:
               showToast(`Unsupported invitation type: ${invitationType}`, 'error');
               return;
         }

         const requestBody = {
            invitationId,
            type: invitationType,
         };

         const response = await fetch(tokenEndpoint, {
            method: 'POST',
            headers,
            body: JSON.stringify(requestBody),
         });

         if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
            showToast(
               `Failed to generate invitation token: ${errorData.error || response.statusText}`,
               'error'
            );
            return;
         }

         const data = await response.json();

         if (!data.success) {
            showToast(`Failed to generate invitation token: ${data.error || 'Unknown error'}`, 'error');
            return;
         }

         // Close notification panel
         if (onNotificationClick) {
            onNotificationClick();
         }

         // Navigate to appropriate invitation page with token
         switch (invitationType) {
            case 'trip':
               navigate(
                  `/accept-invitation?invitationId=${invitationId}&token=${data.token}&type=trip&user_email=${currentUser.email}`
               );
               break;
            case 'event':
               navigate(
                  `/accept-invitation?invitationId=${invitationId}&token=${data.token}&type=event&user_email=${currentUser.email}`
               );
               break;
            case 'album':
               navigate(
                  `/accept-album-invitation?invitationId=${invitationId}&token=${data.token}&user_email=${currentUser.email}`
               );
               break;
            case 'network':
               navigate(
                  `/accept-network-invitation?invitationId=${invitationId}&token=${data.token}&user_email=${currentUser.email}`
               );
               break;
            default:
               showToast(`Unsupported invitation type: ${invitationType}`, 'error');
               break;
         }
      } catch (error: any) {
         console.error('Error viewing invitation:', error);
         showToast(
            `Failed to open invitation: ${error.message || 'Network error. Please try again.'}`,
            'error'
         );
      } finally {
         setLoadingInvitationView(null);
      }
   };

   // Format timestamp
   const formatTimestamp = (timestamp: any) => {
      if (!timestamp) return 'Just now';

      try {
         const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
         return formatDistanceToNow(date, { addSuffix: true });
      } catch (error) {
         return 'Recently';
      }
   };

   // Get notification message with sender info for chat notifications
   const getNotificationMessage = (notification: Notification) => {
      if (notification.type === 'MESSAGE') {
         // Try to get sender name from multiple sources
         const senderName =
            notification.senderInfo?.name ||
            notification.data?.senderName ||
            notification.data?.senderDisplayName ||
            notification.data?.senderUsername ||
            'Someone';

         // Check if this is a group chat notification (has roomId in data)
         if (notification.data?.roomId) {
            const entityType = notification.data?.entityType;
            let entityName = notification.data?.entityName || notification.sourceName;

            // Additional fallbacks for entityName
            if (!entityName || entityName === 'undefined' || entityName === 'null') {
               const roomId = notification.data.roomId;
               if (entityType === 'trip' || roomId.includes('trip')) {
                  entityName = 'Trip Chat';
               } else if (entityType === 'event' || roomId.includes('event')) {
                  entityName = 'Event Chat';
               } else {
                  entityName = 'Group Chat';
               }
            }

            if (entityType === 'trip') {
               return `${senderName} sent a chat message`;
            } else if (entityType === 'event') {
               return `${senderName} sent a chat message`;
            } else {
               return `${senderName} sent a chat message`;
            }
         }

         // Check legacy sourceType format (fallback)
         if (notification.sourceType === 'trip') {
            const tripName = notification.sourceName || 'Trip';
            return `${senderName} sent a message`;
         } else if (notification.sourceType === 'event') {
            const eventName = notification.sourceName || 'Event';
            return `${senderName} sent a message`;
         } else {
            // One-on-one message
            return `${senderName} sent you a message`;
         }
      }

      // Handle other notification types
      if (notification.type === 'TRIP_INVITATION') {
         const inviterName =
            notification.senderInfo?.name ||
            notification.data?.inviterName ||
            notification.data?.senderName ||
            'Someone';
         return `${inviterName} invited you to join ${notification.sourceName || 'a trip'}`;
      }

      if (notification.type === 'EVENT_INVITATION') {
         const inviterName =
            notification.senderInfo?.name ||
            notification.data?.inviterName ||
            notification.data?.senderName ||
            'Someone';
         return `${inviterName} invited you to join ${notification.sourceName || 'an event'}`;
      }

      if (notification.type === 'ALBUM_INVITATION') {
         const inviterName =
            notification.senderInfo?.name ||
            notification.data?.inviterName ||
            notification.data?.senderName ||
            'Someone';
         return `${inviterName} invited you to join ${notification.sourceName || 'an album'}`;
      }

      if (notification.type === 'NETWORK_INVITATION') {
         const inviterName =
            notification.senderInfo?.name ||
            notification.data?.inviterName ||
            notification.data?.senderName ||
            'Someone';
         const relationshipType = notification.data?.relationshipType || 'connection';
         return `${inviterName} sent you a ${relationshipType} request`;
      }

      if (notification.type === 'TRIP_UPDATE') {
         const updaterName =
            notification.senderInfo?.name ||
            notification.data?.updaterName ||
            notification.data?.senderName ||
            'Someone';
         return `${updaterName} updated ${notification.sourceName || 'a trip'}`;
      }

      if (notification.type === 'EVENT_UPDATE') {
         const updaterName =
            notification.senderInfo?.name ||
            notification.data?.updaterName ||
            notification.data?.senderName ||
            'Someone';
         return `${updaterName} updated ${notification.sourceName || 'an event'}`;
      }

      if (notification.type === 'COMMENT') {
         const commenterName =
            notification.senderInfo?.name ||
            notification.data?.commenterName ||
            notification.data?.senderName ||
            'Someone';
         return `${commenterName} commented on ${notification.sourceName || 'a post'}`;
      }

      if (notification.type === 'PHOTO_UPLOAD') {
         const uploaderName =
            notification.senderInfo?.name ||
            notification.data?.uploaderName ||
            notification.data?.senderName ||
            'Someone';
         return `${uploaderName} uploaded a photo to ${notification.sourceName || 'a trip'}`;
      }

      // Default fallback
      return notification.message || 'New notification';
   };

   // Render different notification types
   const renderNotification = (notification: Notification) => {
      const common = (
         <>
            <ListItemAvatar>
               {notification.senderInfo?.photoURL ? (
                  <Avatar
                     src={notification.senderInfo.photoURL}
                     alt={notification.senderInfo.name || 'User'}
                  />
               ) : notification.sourceImage ? (
                  <Avatar
                     src={notification.sourceImage}
                     alt={notification.sourceName || 'Notification'}
                  />
               ) : (
                  <Avatar sx={{ bgcolor: notification.isRead ? 'grey.400' : 'primary.main' }}>
                     {getNotificationIcon(notification.type)}
                  </Avatar>
               )}
            </ListItemAvatar>

            <ListItemText
               primary={
                  <Typography
                     variant='subtitle2'
                     sx={{ fontWeight: notification.isRead ? 'normal' : 'bold' }}
                  >
                     {notification.title}
                  </Typography>
               }
               secondary={
                  <>
                     <Typography
                        variant='body2'
                        component='span'
                        sx={{ display: 'block', color: 'text.primary' }}
                     >
                        {getNotificationMessage(notification)}
                     </Typography>
                     {/* Show location for event invitations */}
                     {notification.type === 'EVENT_INVITATION' && notification.data?.location && formatLocationDisplay(notification.data.location as string | { address?: string; name?: string }) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, mb: 0.5 }}>
                           <LocationOnIcon fontSize='small' color='action' sx={{ mr: 0.5 }} />
                           <Typography variant='caption' color='text.secondary'>
                              {formatLocationDisplay(notification.data.location as string | { address?: string; name?: string })}
                           </Typography>
                        </Box>
                     )}
                     {/* Show emoji for reaction notifications */}
                     {notification.data?.emoji && (
                        <Typography variant='body1' sx={{ mt: 0.5, mb: 0.5 }}>
                           {notification.data.emoji}
                        </Typography>
                     )}
                     <Typography variant='caption' color='text.secondary'>
                        {formatTimestamp(notification.createdAt)}
                     </Typography>
                  </>
               }
               sx={{
                  '& .MuiListItemText-primary': {
                     color: notification.isRead ? 'text.primary' : 'primary.main',
                  },
               }}
            />

            {/* Show loading indicator for message notifications being opened */}
            {notification.type === 'MESSAGE' &&
               loadingChatId ===
                  (notification.sourceId || notification.data?.roomId || 'unknown') && (
                  <Box sx={{ ml: 1 }}>
                     <CircularProgress size={20} />
                  </Box>
               )}

            {/* Show view icon for pending invitations */}
            {['TRIP_INVITATION', 'EVENT_INVITATION', 'ALBUM_INVITATION', 'NETWORK_INVITATION'].includes(notification.type) &&
               notification.invitationData && (
                  <Box sx={{ ml: 1 }}>
                     {loadingInvitationView === notification.id ? (
                        <CircularProgress size={20} />
                     ) : (
                        <ArrowForwardIcon color="action" sx={{ fontSize: 20 }} />
                     )}
                  </Box>
               )}
         </>
      );

      return common;
   };

   if (error) {
      return (
         <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography color='error' variant='body2'>
               {error}
            </Typography>
            <Button size='small' sx={{ mt: 1 }} onClick={() => window.location.reload()}>
               Retry
            </Button>
         </Box>
      );
   }

   if (isLoading) {
      return (
         <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={28} />
         </Box>
      );
   }

   // Handle case where notifications array is undefined or null
   const hasNotifications = Array.isArray(notifications) && notifications.length > 0;

   if (!hasNotifications) {
      return (
         <Box sx={{ p: 3, textAlign: 'center' }}>
            <NotificationIcon color='disabled' sx={{ fontSize: 40, mb: 1 }} />
            <Typography color='textSecondary'>No notifications yet</Typography>
         </Box>
      );
   }

   return (
      <Box
         ref={scrollContainerRef}
         sx={{
            maxHeight,
            overflow: 'auto',
            '&::-webkit-scrollbar': {
               width: '6px',
            },
            '&::-webkit-scrollbar-track': {
               backgroundColor: 'rgba(0,0,0,0.1)',
            },
            '&::-webkit-scrollbar-thumb': {
               backgroundColor: 'rgba(0,0,0,0.2)',
               borderRadius: '3px',
            },
         }}
         onScroll={handleScroll}
      >
         <List disablePadding>
            {notifications.map((notification, index) => (
               <React.Fragment key={notification.id}>
                  <ListItem
                     alignItems='flex-start'
                     sx={{
                        backgroundColor: getBackgroundColor(notification.isRead),
                        cursor: 'pointer',
                        '&:hover': {
                           backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                        py: 1.5,
                     }}
                     onClick={() => handleNotificationClick(notification)}
                  >
                     {renderNotification(notification)}
                  </ListItem>
                  {index < notifications.length - 1 && <Divider component='li' />}
               </React.Fragment>
            ))}

            {/* Loading more indicator */}
            {isLoadingMore && (
               <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={20} />
                  <Typography variant='body2' sx={{ ml: 1 }} color='text.secondary'>
                     Loading more notifications...
                  </Typography>
               </Box>
            )}

            {/* End of list indicator */}
            {!hasMore && notifications.length > 0 && (
               <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant='body2' color='text.secondary'>
                     You've reached the end of your notifications
                  </Typography>
               </Box>
            )}
         </List>

         {/* Toast Notification */}
         <Snackbar
            open={toast.open}
            autoHideDuration={4000}
            onClose={closeToast}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
         >
            <Alert onClose={closeToast} severity={toast.severity} sx={{ width: '100%' }}>
               {toast.message}
            </Alert>
         </Snackbar>
      </Box>
   );
};

export default NotificationList;
